import { useCallback, useEffect, useRef, useState } from "react";
import { decodeG711Frame, G711_RATE, PcmFrom8k } from "../lib/g711";
import { startLiveVoicePlayer, type LiveVoicePlayer } from "../lib/pcm-playback";
import { G711Assembler, parseVoicePacket, voiceAgeMs, type G711Codec } from "../lib/voice-frame";
import {
  audioDebugEnabled,
  publishVoiceStats,
  type VoiceStats,
} from "../lib/audio-debug";
import { hasNativeBridge } from "../lib/native-bridge";
import { subscribeForeground } from "../lib/page-lifecycle";
import {
  nativeVoiceAvailable,
  nativeVoiceStatus,
  pushNativeG711,
  setNativeMuted,
  startNativePlayback,
  stopNativePlayback,
} from "../lib/native-voice";
import { pttLog } from "../lib/ptt-log";
import { masterSocketUrl } from "../api";
import { logLiveStep } from "../lib/live-trace";

export function listenWsUrl(deviceId: string): string {
  return masterSocketUrl(`/ws/listen/${encodeURIComponent(deviceId)}`);
}

export function useLiveListen(opts: {
  deviceId: string;
  channel: number;
  enabled: boolean;
  muted: boolean;
  paused: boolean;
}): { unlock: () => void; stats: VoiceStats | null } {
  const { deviceId, channel, enabled, muted, paused } = opts;
  const mutedRef = useRef(muted);
  const pausedRef = useRef(paused);
  const playerRef = useRef<LiveVoicePlayer | null>(null);
  const gestureRef = useRef<Promise<LiveVoicePlayer> | null>(null);
  const nativeRef = useRef(hasNativeBridge() && /Android/i.test(navigator.userAgent || ""));
  const [stats, setStats] = useState<VoiceStats | null>(null);
  mutedRef.current = muted;
  pausedRef.current = paused;

  const speakerOff = () => mutedRef.current || pausedRef.current;

  const applySpeaker = useCallback(() => {
    const off = speakerOff();
    if (nativeRef.current) setNativeMuted(off);
    playerRef.current?.setMuted(off);
  }, []);

  const unlock = useCallback(() => {
    if (nativeRef.current) {
      void startNativePlayback();
      setNativeMuted(speakerOff());
      return;
    }
    if (!gestureRef.current) {
      gestureRef.current = startLiveVoicePlayer().then((player) => {
        playerRef.current = player;
        player.setMuted(speakerOff());
        return player;
      });
    }
    void gestureRef.current.then((player) => {
      player.setMuted(speakerOff());
    });
  }, []);

  useEffect(() => {
    applySpeaker();
  }, [muted, paused, applySpeaker]);

  useEffect(() => {
    void nativeVoiceAvailable().then((ok) => {
      nativeRef.current = ok;
      if (ok) publishVoiceStats({ mode: "native", outRate: 8000 });
    });
  }, []);

  useEffect(() => {
    return () => {
      const player = playerRef.current;
      playerRef.current = null;
      gestureRef.current = null;
      player?.stop();
      void stopNativePlayback();
    };
  }, []);

  useEffect(() => {
    if (!enabled || !deviceId) return;
    let closed = false;
    let ws: WebSocket | null = null;
    const assembler = new G711Assembler();
    const upByRate = new Map<number, PcmFrom8k>();
    let packets = 0;
    let lost = 0;
    let reordered = 0;
    let lastSeq = -1;
    let codec: G711Codec = "pcma";
    let resampleIn = 0;
    let resampleOut = 0;
    let windowStart = performance.now();
    let windowCount = 0;
    let pps = 0;
    let lastBytes = 0;
    let lastTs = 0;
    let enqueueAt = 0;
    let listenAgeMs = 0;
    let e2eEstMs = 0;
    let listenPacketReceiveMs = 0;

    if (nativeRef.current) {
      void startNativePlayback().then((ok) => {
        if (ok) setNativeMuted(speakerOff());
      });
    }

    const playG711 = (frame: Uint8Array) => {
      listenPacketReceiveMs = performance.now();
      if (nativeRef.current) {
        pushNativeG711(frame, codec);
        enqueueAt = performance.now();
        return;
      }
      const pcm8k = decodeG711Frame(frame, codec);
      // Mild makeup so browser playback matches native AudioTrack loudness
      // without pumping (AGC is capture-side only).
      for (let i = 0; i < pcm8k.length; i++) {
        const v = pcm8k[i]! * 1.35;
        pcm8k[i] = v > 1 ? 1 : v < -1 ? -1 : v;
      }
      const player = playerRef.current;
      if (!player) return;
      resampleIn += pcm8k.length;
      const up = upByRate.get(player.sampleRate) ?? new PcmFrom8k(player.sampleRate);
      upByRate.set(player.sampleRate, up);
      const pcm = player.sampleRate !== G711_RATE ? up.process(pcm8k) : pcm8k;
      resampleOut += pcm.length;
      enqueueAt = performance.now();
      player.pushPcm(pcm);
    };

    const report = (wsState: string) => {
      const player = playerRef.current;
      const apply = (native: Record<string, unknown> | null) => {
        const n = (key: string) => {
          const v = native?.[key];
          return typeof v === "number" && Number.isFinite(v) ? v : undefined;
        };
        const row = publishVoiceStats({
          codec,
          g711Codec: codec,
          packetSize: lastBytes,
          jt1078PayloadLength: lastBytes > 8 ? lastBytes - 8 : lastBytes,
          g711FrameBytes: 160,
          seq: lastSeq,
          ts: lastTs,
          pps,
          lost,
          reordered,
          jitterMs: player?.getQueueMs() ?? n("queueMs") ?? 0,
          queueMs: player?.getQueueMs() ?? n("queueMs") ?? 0,
          outRate: nativeRef.current ? 8000 : player?.sampleRate ?? 0,
          resampleIn,
          resampleOut,
          underrun: player?.getUnderruns() ?? n("underrun") ?? 0,
          overrun: player?.getOverruns() ?? n("overrun") ?? 0,
          mode: nativeRef.current ? "native" : player?.mode ?? "none",
          muted: speakerOff(),
          ws: wsState,
          e2eEstMs,
          listenAgeMs,
          enqueueAt,
          playAt: enqueueAt,
          listenPacketReceiveMs,
          receiveAt: Date.now(),
          staleFrames: player?.getOverruns() ?? n("staleFrames") ?? n("droppedFrames") ?? 0,
          decodedPcmRms: n("decodedPcmRms") ?? 0,
          audioTrackBufferSize: n("audioTrackBufferSize") ?? 0,
          playbackQueueSize: n("playbackQueueSize") ?? player?.getQueueMs() ?? 0,
          droppedFrames: player?.getOverruns() ?? n("droppedFrames") ?? 0,
          malformedFrames: n("malformedFrames") ?? 0,
          invalidPayloadCount: n("invalidPayloadCount") ?? 0,
        });
        if (audioDebugEnabled()) setStats({ ...row });
      };
      if (nativeRef.current) {
        void nativeVoiceStatus().then((st) => apply(st));
        return;
      }
      apply(null);
    };

    const stopWs = () => {
      closed = true;
      if (retry) clearTimeout(retry);
      try {
        if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ action: "stop" }));
      } catch {
        /* ignore */
      }
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      ws = null;
    };

    let retry: ReturnType<typeof setTimeout> | null = null;
    let delay = 1000;

    const connect = () => {
      if (closed) return;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
      logLiveStep({ vehicleId: deviceId, channel, step: "listen_ws" });
      const socket = new WebSocket(listenWsUrl(deviceId));
      ws = socket;
      socket.binaryType = "arraybuffer";
      socket.onopen = () => {
        if (closed || ws !== socket) return;
        logLiveStep({ vehicleId: deviceId, channel, step: "listen_ws_open" });
        delay = 1000;
        socket.send(JSON.stringify({ action: "start", channel }));
        publishVoiceStats({ ws: "open", muted: speakerOff() });
      };
      socket.onmessage = (ev) => {
        if (closed || ws !== socket || typeof ev.data === "string") return;
        const bytes = new Uint8Array(ev.data as ArrayBuffer);
        const parsed = parseVoicePacket(bytes);
        if (!parsed) return;
        lastBytes = bytes.length;
        lastTs = parsed.t;
        codec = parsed.codec;
        listenAgeMs = voiceAgeMs(parsed.t);
        e2eEstMs = listenAgeMs + (playerRef.current?.getQueueMs() ?? 0);
        if (parsed.seq >= 0 && lastSeq >= 0) {
          const expected = (lastSeq + 1) & 0xffff;
          const gap = (parsed.seq - expected) & 0xffff;
          if (gap !== 0 && gap < 8) {
            lost += gap;
          } else if (gap > 0xff00) {
            reordered += 1;
            return;
          }
        }
        if (parsed.seq >= 0) lastSeq = parsed.seq;
        const frames = assembler.push(parsed.payload);
        packets += frames.length;
        windowCount += frames.length;
        const now = performance.now();
        if (now - windowStart >= 1000) {
          pps = windowCount;
          windowCount = 0;
          windowStart = now;
        }
        for (const frame of frames) playG711(frame);
        if (packets === 1 || packets % 50 === 0) {
          report(ws?.readyState === WebSocket.OPEN ? "open" : "down");
          pttLog("listen", {
            packets,
            codec,
            bytes: lastBytes,
            seq: lastSeq,
            lost,
            reordered,
            pps,
            native: nativeRef.current,
            muted: speakerOff(),
            queueMs: playerRef.current?.getQueueMs() ?? 0,
            ageMs: listenAgeMs,
            e2eEstMs,
          });
        }
      };
      socket.onerror = () => {
        logLiveStep({ vehicleId: deviceId, channel, step: "listen_ws", error: "connect_failed" });
      };
      socket.onclose = () => {
        logLiveStep({ vehicleId: deviceId, channel, step: "disconnect", error: "listen_ws" });
        if (ws === socket) ws = null;
        if (closed) return;
        publishVoiceStats({ ws: "closed" });
        retry = setTimeout(connect, delay);
        delay = Math.min(delay * 2, 15000);
      };
    };

    connect();
    const stopFg = subscribeForeground(() => {
      delay = 1000;
      if (retry) clearTimeout(retry);
      connect();
    });

    const iv = window.setInterval(() => report(ws?.readyState === WebSocket.OPEN ? "open" : "down"), 1000);

    return () => {
      window.clearInterval(iv);
      stopFg();
      stopWs();
    };
  }, [deviceId, channel, enabled]);

  return { unlock, stats };
}
